import { useState } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useMaturityFramework } from '../../../graphql/queries';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../../components/shared';
import { deleteMaturityFramework, ApiError } from '../../../lib/apiClient';
import { LIST_MATURITY_FRAMEWORKS } from '../../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './$frameworkId.css';

export const Route = createFileRoute('/maturity/frameworks/$frameworkId')({
  component: MaturityFrameworkDetailPage,
});

export { MaturityFrameworkDetailPage };

function MaturityFrameworkDetailPage(): JSX.Element {
  const { frameworkId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, loading, error: fetchError } = useMaturityFramework(frameworkId);
  const framework = data?.maturityFramework;

  const CardElement = 'forge-card' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  // Edit is a child route; render only the outlet. Must stay after every hook (#1539).
  if (location.pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  if (loading)
    return (
      <div className="framework-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );

  if (fetchError) {
    return (
      <div className="framework-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load framework"
          description={fetchError.message}
        />
      </div>
    );
  }

  if (!framework) {
    return (
      <div className="framework-detail-page">
        <EmptyState
          icon="search_off"
          title="Framework not found"
          description={`No framework found with ID: ${frameworkId}`}
          actionLabel="Back to Frameworks"
          onAction={() => void navigate({ to: '/maturity/frameworks' })}
        />
      </div>
    );
  }

  const totalCategories = framework.areas.reduce((acc, a) => acc + a.categories.length, 0);
  const totalCapabilities = framework.areas.reduce(
    (acc, a) => acc + a.categories.reduce((cacc, c) => cacc + c.capabilities.length, 0),
    0
  );

  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteMaturityFramework(frameworkId);
      await apolloClient.refetchQueries({ include: [LIST_MATURITY_FRAMEWORKS] });
      void navigate({ to: '/maturity/frameworks' });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete framework';
      setError(message);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <div className="framework-detail-page">
      {error && (
        <BannerElement theme="danger">
          <span>{error}</span>
        </BannerElement>
      )}

      <div className="page-header page-header--toolbar">
        <div className="page-header-left">
          <Link to="/maturity/frameworks" className="back-link">
            ← Frameworks
          </Link>
          <h1 className="forge-typography--headline5">{framework.name}</h1>
          <span className={`status-badge status-badge--${framework.status.toLowerCase()}`}>
            {framework.status}
          </span>
        </div>

        <div className="page-header-actions">
          <PermissionGate permission="maturityFramework:update">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() =>
                void navigate({
                  to: '/maturity/frameworks/$frameworkId/edit',
                  params: { frameworkId },
                })
              }
            >
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="maturityFramework:delete">
            <ForgeButton type="button" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <CardElement className="framework-detail-page__overview">
        <h2 className="forge-typography--subtitle1">Overview</h2>
        <dl className="detail-grid">
          <dt>Version</dt>
          <dd>{framework.version}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`status-badge status-badge--${framework.status.toLowerCase()}`}>
              {framework.status}
            </span>
          </dd>
          <dt>Effective From</dt>
          <dd>{new Date(framework.effectiveFrom).toLocaleDateString()}</dd>
          {framework.effectiveTo && (
            <>
              <dt>Effective To</dt>
              <dd>{new Date(framework.effectiveTo).toLocaleDateString()}</dd>
            </>
          )}
          <dt>Areas</dt>
          <dd>{framework.areas.length}</dd>
          <dt>Categories</dt>
          <dd>{totalCategories}</dd>
          <dt>Capabilities</dt>
          <dd>{totalCapabilities}</dd>
        </dl>
        {framework.description && (
          <p className="forge-typography--body2 framework-detail-page__description">
            {framework.description}
          </p>
        )}
      </CardElement>

      {framework.areas.map((area) => (
        <CardElement key={area.id} className="framework-detail-page__area">
          <h2 className="forge-typography--subtitle1">{area.name}</h2>
          {area.categories.map((cat) => (
            <div key={cat.id} className="framework-detail-page__category">
              <h3 className="forge-typography--subtitle2">{cat.name}</h3>
              <ul className="framework-detail-page__capabilities">
                {cat.capabilities.map((cap) => (
                  <li key={cap.id} className="framework-detail-page__capability">
                    <span className="framework-detail-page__cap-name">{cap.name}</span>
                    <span className="framework-detail-page__cap-levels">
                      {cap.levels.length} levels
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardElement>
      ))}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Framework"
        message={`Are you sure you want to delete this framework? This action cannot be undone.`}
        itemName={framework.name}
        isDeleting={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
}
